/**
 * @author Muhammad Fahad
 * @datetime April, 20th 2022
 * @description: This is component is for listing building names so that user can see details
 *                of that building by clicking on it.
 */
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { SharedDataService } from 'src/app/shared/services/shared-data.service';
import { HelperService } from 'src/app/shared/utils/helper.service';
import { BuildingManagementService } from 'src/app/building-managment/building-management.service';
import { SiteManagerComponent } from 'src/app/site-management/site-manager/site-manager.component';
import { MatDialog } from '@angular/material/dialog';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { FloorManagementService } from 'src/app/floor-management/floor-management.service';
import { NgRedux } from '@angular-redux/store';
import { IAppState } from 'src/app/shared/state-management/state-interfaces';
import { StateManagementService } from 'src/app/shared/services/state-management.service';
import { CommonService } from 'src/app/shared/utils/common.service';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { TranslateService } from '@ngx-translate/core';
import { SiteSettingsComponent } from 'src/app/site-management/site-settings/site-settings.component';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-building-list',
  templateUrl: './building-list.component.html',
  styleUrls: ['./building-list.component.scss'],
})
export class BuildingListComponent implements OnInit, OnDestroy {
  @Input() buildingsList: any = [];
  subscription: Subscription | undefined;
  dialogRef: MatDialog | undefined;
  sharedData: any;
  CONSTANTS = CONSTANTS;
  readonly LANG_CONST = LANG_CONST;
  message = this.translate.instant('FAILED_TO_LOAD_BUILDINGS');
  isDisabledClickBuilding = false; // disabled click event util data load of Building
  buildingSubscription: Subscription | undefined; // disabled click event util data load of floor and activity

  /**
   * @description injecting services to the constructor
   * @param { HelperService } helperService
   * @param { SharedDataService } sharedDataService
   * @param { BuildingManagementService } buildingManagementService
   */
  constructor(
    private helperService: HelperService,
    public sharedDataService: SharedDataService,
    public buildingManagementService: BuildingManagementService,
    public floorManagementService: FloorManagementService,
    public stateManagementService: StateManagementService,
    public commonService: CommonService,
    private localizationService: LocalizationService,
    private translate: TranslateService
  ) {}

  /**
   * @description subscribe shared data service and update building list if building name is modified
   */
  ngOnInit(): void {
    this.localizationService.setupLocalization();
    this.subscription = this.sharedDataService.currentData.subscribe(
      (sharedData) => {
        const index = this.buildingsList?.findIndex(
          (building: any) =>
            building?.buildingId === sharedData?.selectedBuilding?.buildingId
        );
        if (
          index > -1 &&
          this.buildingsList[index]?.buildingId ===
            sharedData?.selectedBuilding?.buildingId &&
          this.buildingsList[index]?.buildingName !==
            sharedData?.selectedBuilding?.buildingName
        ) {
          this.buildingsList[index].buildingName =
            sharedData?.selectedBuilding?.buildingName;
          this.buildingsList = [...this.buildingsList]; //for sorting hack
        }
        this.sharedData = sharedData;
      }
    );

    this.buildingManagementService.triggerRerender.subscribe(() => {
      this.buildingsList = this.stateManagementService.getBuildings();
    });

    this.buildingSubscription =
      this.commonService.isLoadingBuildingSubject.subscribe(
        (status: boolean) => {
          this.isDisabledClickBuilding = false;
        }
      );
  }

  /**
   * @description this method opens Site Settings modal for environments
   *              where full site settings flow is enabled.
   *              In other environments, it navigates directly to Edit Site flow.
   */
  openSiteModal(): void {
    if (environment.siteSettingsEnabled) {
      this.helperService.openModalDialog(
        SiteSettingsComponent,
        this.sharedData?.selectedSite,
        CONSTANTS.SITE_SETTINGS_MODAL
      );
    } else {
      const dialogInstance = this.helperService.openModalDialog(
        SiteManagerComponent,
        this.sharedData?.selectedSite,
        CONSTANTS.FIXED_SCREEN_MODAL
      );
      dialogInstance.afterClosed().subscribe((result: boolean) => {
        if (result) {
          this.floorManagementService.setTriggerRerender(true);
        }
        this.buildingManagementService
          .getAllBuildingsBySiteId(this.sharedData?.selectedSite?.siteId)
          .subscribe({
            next: (res: any) => {
              this.buildingsList = res;
              if (this.buildingsList.length <= 0) {
                this.buildingManagementService.buildingsView = false;
              } else this.buildingManagementService.buildingsView = true;
            },
          });
      });
    }
  }

  /**
   * @description unsubscribe the shared data subscription
   */
  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.buildingSubscription?.unsubscribe();
  }

  /**
   * @description this method pass as parameter to perform retry action
   */
  retryAction = () => {
    window.location.reload();
  };

  trackByOption(index: number, option: any) {
    return option;
  }
}
